import React, { useState } from 'react'
import { Keyboard, ScrollView, View } from 'react-native'
import { ImageStyle } from 'react-native-fast-image'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRoute, RouteProp } from '@react-navigation/native'
import { useDispatch } from 'react-redux'
import pluralize from 'pluralize'
import { capitalize, mapValues } from 'lodash'
import { StyleService, useStyleSheet } from '@src/style/service'
import { NS } from '@assets/svgs'
import { Text, Button, Icon } from '@components/base'
import { AppStackParamList } from '@navigation/types'
import { SaveTargetKind } from '@screens/Ingredients/constants/saveTarget'
import { useNoraStreamChat } from '@screens/Nora/context/NoraStreamChatContext'
import {
  LogMealDraftIngredientUpdate,
  replaceIngredientInLogMealDraft,
} from '@screens/Chat/utils/logMealDraft'
import { useGoBack } from '@utils/navigation'
import { useAvailableScreenHeight } from '@src/utils/hooks'
import Bugsnag from '@src/config/bugsnag'
import { LoadingFastImage } from '@src/components/LoadingFastImage'
import { GestureDismissibleModal } from '@src/components/GestureDismissibleModal'
import { IngredientServingOption } from '../types'
import { IngredientPortionForm } from '../components/IngredientPortionForm'
import { NutritionFacts } from '../components/NutritionFacts'

export const IngredientPortion = () => {
  const styles = useStyleSheet(themedStyle)
  const dispatch = useDispatch()
  const goBack = useGoBack()
  const availableHeight = useAvailableScreenHeight()
  const { saveCurrentActionTunnelDraft } = useNoraStreamChat()

  const { params } = useRoute<RouteProp<AppStackParamList, 'IngredientPortion'>>()
  const { ingredient, isUpdatingIngredient, saveTarget } = params

  const [quantityText, setQuantityText] = useState(`${ingredient.servingAmount || 1}`)
  const [servingName, setServingName] = useState(pluralize.singular(ingredient.servingUnits || ''))
  const [isNutritionVisible, setNutritionVisible] = useState(false)
  const [isSaving, setSaving] = useState(false)

  const quantity = Number.parseFloat(quantityText) || 0
  const selectedServing: IngredientServingOption =
    ingredient.servingOptions.find(
      ({ name }) => name.toLowerCase() === servingName.toLowerCase(),
    ) || ingredient.servingOptions[0]
  const nutrition = mapValues(selectedServing.nutritionPerServing, (value) => value * quantity)

  const onAddIngredient = async () => {
    setSaving(true)

    const updatedDraftIngredient: LogMealDraftIngredientUpdate = {
      calories: nutrition.calories,
      nutrition,
      servingAmount: quantity,
      servingUnits: selectedServing.name,
    }

    // We are using this component inside Nora chat due to an active log meal action tunnel.
    if (saveTarget?.kind === SaveTargetKind.ACTION_TUNNEL) {
      try {
        await saveCurrentActionTunnelDraft({
          draftUpdater: (sourceDraft) =>
            replaceIngredientInLogMealDraft(
              sourceDraft,
              saveTarget.ingredientId,
              updatedDraftIngredient,
            ),
          messageId: saveTarget.messageId,
        })
      } catch (error: any) {
        setSaving(false)
        Bugsnag.notify(error)
        return
      }
      goBack()
      return
    }

    const updatedIngredient = {
      ...ingredient,
      ...updatedDraftIngredient,
      // Prevent automatic update of ingredient serving/quantity when the meal description is updated
      isFromSuggestions: false,
    }

    dispatch({
      type: isUpdatingIngredient ? 'meals/updateDraftIngredient' : 'meals/addDraftIngredient',
      payload: updatedIngredient,
    })

    goBack()
  }

  const image = ingredient.imageUrl ? (
    <LoadingFastImage source={{ uri: ingredient.imageUrl }} style={styles.image as ImageStyle} />
  ) : (
    <NS width={56} height={56} />
  )

  const insets = useSafeAreaInsets()

  const onSeeNutritionFactsPress = () => {
    Keyboard.dismiss()
    setNutritionVisible(true)
  }

  let submitButtonText = 'Add'
  if (isUpdatingIngredient) {
    submitButtonText = 'Save'
  }
  if (isSaving) {
    submitButtonText = 'Saving...'
  }

  return (
    <GestureDismissibleModal
      containerStyle={styles.modalContainer}
      contentContainerStyle={[styles.modalContent, { paddingBottom: Math.max(insets.bottom, 16) }]}
      onDismiss={goBack}
      dismissButtonText="Cancel"
    >
      <View style={styles.header}>
        {image}
        <Text type="title-3" style={styles.title}>
          {ingredient.description}
        </Text>
        <Text type="regular" bold style={styles.calories}>
          {`${Number(nutrition.calories.toFixed(2))} cal`}
        </Text>
      </View>
      <IngredientPortionForm
        autoFocus
        serving={selectedServing.name}
        servingOptions={ingredient.servingOptions.map(({ name }) => capitalize(name)).sort()}
        style={styles.portionDetails}
        quantity={quantityText}
        onServingChange={setServingName}
        onQuantityChange={setQuantityText}
      />
      {isNutritionVisible ? (
        <View style={styles.nutritionFacts}>
          <Text type="small" bold style={styles.nutritionTitle}>
            Nutrition Facts
          </Text>
          <ScrollView
            showsVerticalScrollIndicator={false}
            style={{ maxHeight: availableHeight - 310 }}
          >
            <NutritionFacts hideMicroNutrients nutrition={nutrition} />
          </ScrollView>
        </View>
      ) : (
        <Button
          type="transparent"
          size="small"
          accessibilityLabel="See Nutrition Facts"
          onPress={onSeeNutritionFactsPress}
          style={styles.toggleNutritionButton}
          accessoryRight={<Icon name="arrow-right" />}
        >
          See Nutrition Facts
        </Button>
      )}
      <Button
        type="primary"
        size="block"
        disabled={!quantity || !servingName || isSaving}
        accessibilityLabel={isUpdatingIngredient ? 'Save Ingredient' : 'Add Ingredient'}
        onPress={onAddIngredient}
      >
        {submitButtonText}
      </Button>
    </GestureDismissibleModal>
  )
}

const themedStyle = StyleService.create({
  modalContainer: {
    flex: 0,
    flexBasis: 'auto',
    marginTop: 'auto',
  },
  modalContent: {
    flex: 0,
    paddingHorizontal: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  image: {
    height: 56,
    width: 56,
  },
  title: {
    flex: 1,
    marginLeft: 16,
    marginRight: 12,
  },
  calories: {
    color: 'theme.text.secondary',
  },
  portionDetails: {
    marginTop: 24,
    marginBottom: 16,
  },
  nutritionFacts: {
    marginBottom: 16,
  },
  nutritionTitle: {
    marginVertical: 8,
  },
  toggleNutritionButton: {
    marginRight: 'auto',
    marginBottom: 16,
  },
})
